import { currentLoggedUserVar } from '@/shared/api';
import { useReactiveVar } from '@apollo/client';
import type { SxProps, Theme } from '@mui/material';
import { Box, ButtonBase, Stack, Tooltip } from '@mui/material';
import type { SyntheticEvent } from 'react';
import { useEffect, useState } from 'react';
import useSwitchCompaniesDialog from './Dialogs/Company/useSwitchCompaniesDialog';
import { Small } from './Typography';

/*
 * Pharmacy logos arrive in every shape -- wide wordmarks, square seals, tall crests -- so fitting
 * them all to one height makes a 6:1 wordmark six times the size of a seal beside it. Instead each
 * logo is sized to the same *area*, which is what the eye reads as "the same size":
 *
 *   height = sqrt(TARGET_AREA / aspect), width = height * aspect
 *
 * then clamped to the slot. A square seal lands at 26x26 (height cap), a 4:1 wordmark at 88x22 and a
 * 6:1 one at 107x18 -- wide logos sit shorter and square ones taller, and all carry about the same
 * visual weight.
 */
const TARGET_AREA = 30 * 64;
const MAX_HEIGHT = 26;
const MAX_WIDTH = 128;

const sizeForAspect = (aspect: number) => {
  let height = Math.sqrt(TARGET_AREA / aspect);
  let width = height * aspect;
  if (height > MAX_HEIGHT) {
    height = MAX_HEIGHT;
    width = height * aspect;
  }
  if (width > MAX_WIDTH) {
    width = MAX_WIDTH;
    height = width / aspect;
  }
  return { width: Math.round(width), height: Math.round(height) };
};

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0]?.toUpperCase())
    .join('');

type Props = { sx?: SxProps<Theme> };

/**
 * The pharmacy the user is working in, as an always-visible cue for people who switch between
 * several. Logo plus name, never the logo alone: the name survives a missing or broken logo and
 * does not depend on recognising a mark.
 *
 * For someone with access to other pharmacies it is also the way to switch: clicking it opens the
 * same Switch Companies dialog as the profile menu. Everyone else gets a plain label, not a button
 * that does nothing.
 */
const PharmacyFooterBadge = ({ sx }: Props) => {
  const currentUser = useReactiveVar(currentLoggedUserVar);
  const showSwitchCompaniesDialog = useSwitchCompaniesDialog();
  const canSwitch = Boolean(currentUser?.hasMultipleRelyingParties);
  const pharmacy = currentUser?.relyingParty;
  const logoUrl = pharmacy?.logoUrl;
  const name = pharmacy?.name ?? '';

  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const [failed, setFailed] = useState(false);

  // Switching pharmacy swaps the logo; measure the new one from scratch.
  useEffect(() => {
    setSize(null);
    setFailed(false);
  }, [logoUrl]);

  if (!pharmacy) return null;

  const onLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    if (naturalWidth > 0 && naturalHeight > 0) setSize(sizeForAspect(naturalWidth / naturalHeight));
  };

  const showLogo = Boolean(logoUrl) && !failed;

  return (
    <Tooltip title={canSwitch ? `Working in ${name} \u00b7 click to switch pharmacy` : `Working in ${name}`} placement="top-start">
      <Stack
        direction="row"
        spacing={1}
        {...(canSwitch && {
          component: ButtonBase,
          onClick: showSwitchCompaniesDialog,
          'aria-label': `Working in ${name}. Switch pharmacy`
        })}
        sx={[
          { alignItems: 'center', minWidth: 0 },
          canSwitch && {
            borderRadius: 1,
            px: 0.5,
            mx: -0.5,
            font: 'inherit',
            '&:hover, &.Mui-focusVisible': { backgroundColor: 'action.hover' }
          },
          ...(Array.isArray(sx) ? sx : [sx])
        ]}
      >
        {/*
          A white plate behind every logo. Many logos carry their own white box; on the frosted
          footer some would show it and some would not, so every logo gets the same backing.
        */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            height: MAX_HEIGHT + 6,
            minWidth: MAX_HEIGHT + 6,
            px: 0.5,
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper'
          }}
        >
          {showLogo ? (
            <Box
              component="img"
              src={logoUrl}
              alt=""
              onLoad={onLoad}
              onError={() => setFailed(true)}
              sx={{
                display: 'block',
                objectFit: 'contain',
                // Until measured, fall back to a plain fit so the first frame is not a full-size flash.
                width: size?.width ?? 'auto',
                height: size?.height ?? MAX_HEIGHT,
                maxWidth: MAX_WIDTH
              }}
            />
          ) : (
            <Small sx={{ fontSize: 11, fontWeight: 700, color: 'text.primary' }}>{initialsOf(name)}</Small>
          )}
        </Box>
        <Small
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {name}
        </Small>
      </Stack>
    </Tooltip>
  );
};

export default PharmacyFooterBadge;
